import { useSyncExternalStore, useCallback } from 'react';

interface PersistedLayoutPrefs {
  leftCollapsed?: boolean;
  rightOpenDesktop?: boolean;
}

interface LayoutState {
  leftCollapsed: boolean;
  rightOpen: boolean;
}

const STORAGE_KEY = 'helios.prefs.v2:layout';

function getInitialState(): LayoutState {
  if (typeof window === 'undefined') {
    return { leftCollapsed: false, rightOpen: true };
  }

  const width = window.innerWidth;
  const isWide = width >= 1280;
  const isTablet = width >= 768 && width < 1280;

  let saved: PersistedLayoutPrefs = {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) saved = JSON.parse(raw);
  } catch {}

  const leftCollapsed = saved.leftCollapsed !== undefined
    ? saved.leftCollapsed
    : isTablet; // default collapsed icon rail on tablets

  // Right sheet/rail is strictly false on mobile/tablet on initial load
  const rightOpen = isWide ? (saved.rightOpenDesktop ?? true) : false;

  return { leftCollapsed, rightOpen };
}

let currentState: LayoutState = getInitialState();
const listeners = new Set<() => void>();

function persist(state: LayoutState) {
  if (typeof window === 'undefined') return;
  try {
    const width = window.innerWidth;
    const isWide = width >= 1280;
    const data: PersistedLayoutPrefs = {
      leftCollapsed: state.leftCollapsed,
      rightOpenDesktop: isWide ? state.rightOpen : undefined,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

function update(fn: (prev: LayoutState) => LayoutState) {
  currentState = fn(currentState);
  persist(currentState);
  listeners.forEach((cb) => {
    try {
      cb();
    } catch (err) {
      console.error('layoutPrefs listener error:', err);
    }
  });
}

export const layoutStore = {
  subscribe(cb: () => void) {
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  },
  getSnapshot() {
    return currentState;
  },
  set(fn: (prev: LayoutState) => LayoutState) {
    update(fn);
  },
};

export function useLayoutPrefs() {
  const state = useSyncExternalStore(
    layoutStore.subscribe,
    layoutStore.getSnapshot,
    layoutStore.getSnapshot
  );

  const toggleLeft = useCallback(() => {
    update((prev) => ({ ...prev, leftCollapsed: !prev.leftCollapsed }));
  }, []);

  const toggleRight = useCallback(() => {
    update((prev) => ({ ...prev, rightOpen: !prev.rightOpen }));
  }, []);

  const setRightOpen = useCallback((open: boolean) => {
    update((prev) => ({ ...prev, rightOpen: open }));
  }, []);

  const setLeftCollapsed = useCallback((collapsed: boolean) => {
    update((prev) => ({ ...prev, leftCollapsed: collapsed }));
  }, []);

  return {
    ...state,
    toggleLeft,
    toggleRight,
    setRightOpen,
    setLeftCollapsed,
  };
}
